<script setup>
import { computed } from 'vue';
import { useDropdown } from '@/composables/use-dropdown';
import SportLiveTvDropdownTransition from '@/sport-live-tv/components/sport-live-tv-dropdown-transition.vue';
import SportLiveTvDropdownTrigger from '@/sport-live-tv/components/sport-live-tv-dropdown-trigger.vue';
import { SPORT_LIVE_TV_SORT_OPTIONS } from '@/sport-live-tv/constants';
import { useSportLiveTvTranslation } from '@/sport-live-tv/composables/use-sport-live-tv-translation';

const props = defineProps({
  modelValue: { type: String, required: true },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useSportLiveTvTranslation();
const { isOpen, dropdownRef, close, toggle } = useDropdown();

const selectedOption = computed(() => SPORT_LIVE_TV_SORT_OPTIONS.find(({ value }) => value === props.modelValue) ?? SPORT_LIVE_TV_SORT_OPTIONS[0]);

const select = (value) => {
  emit('update:modelValue', value);
  close();
};
</script>

<template>
  <div ref="dropdownRef" class="sport-live-tv-sort-select">
    <SportLiveTvDropdownTrigger :label="t(selectedOption.labelKey)" :open="isOpen" aria-haspopup="listbox" @click="toggle" />
    <SportLiveTvDropdownTransition>
      <ul v-if="isOpen" class="sport-live-tv-sort-select__panel" role="listbox" :aria-label="t('sort')">
        <li v-for="option in SPORT_LIVE_TV_SORT_OPTIONS" :key="option.value" role="none">
          <button
            type="button"
            role="option"
            class="sport-live-tv-sort-select__option"
            :class="{ 'sport-live-tv-sort-select__option--selected': option.value === modelValue }"
            :aria-selected="option.value === modelValue"
            @click="select(option.value)"
          >
            {{ t(option.labelKey) }}
          </button>
        </li>
      </ul>
    </SportLiveTvDropdownTransition>
  </div>
</template>

<style lang="scss" scoped>
.sport-live-tv-sort-select {
  position: relative;

  @include mobile-sm-max {
    width: 100%;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 100%;
    padding: 8px;
    border-radius: 12px;
    background-color: $black18;
    box-shadow: 0 8px 32px rgba($black, 0.6);
    list-style: none;
  }

  &__option {
    width: 100%;
    padding: 8px 12px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: $white;
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: $black50;
    }

    &--selected {
      color: $bg-red;
      font-weight: 700;
    }
  }
}
</style>
